import { CODE_LENGTH, SHIP_STATS } from '../../shared/constants';
import { getOrderableShipsForPlayer } from '../../shared/engine/util';
import { hexKey } from '../../shared/hex';
import { normalizePlayerToken, normalizeRoomCode } from '../../shared/ids';
import { computeCourse } from '../../shared/movement';
import type { SolarSystemMap } from '../../shared/types/domain';
import { initAudio, isMuted, setMuted } from '../audio';
import { byId, hide } from '../dom';
import { isClientFeatureEnabled } from '../feature-flags';
import {
  bindGameClientBrowserEvents,
  bindServiceWorkerControllerReload,
} from '../game-client-browser';
import type { InputHandler } from '../input';
import { TOAST } from '../messages/toasts';
import type { Renderer } from '../renderer/renderer';
import type { UIEvent } from '../ui/events';
import type { UIManager } from '../ui/ui';
import { startVersionCheck } from '../version-check';
import type { KeyboardAction } from './keyboard';
import { deriveKeyboardAction } from './keyboard';
import { getOrdnanceActionableShipIds } from './ordnance';
import type { ClientState } from './phase';
import type { KeyboardPlanningSnapshot } from './planning';
import { buildGameRoute } from './session-links';
import type { ClientSession } from './session-model';

type BrowserToastType = 'error' | 'info' | 'success';
type GamepadShortcutControl =
  | 'confirm'
  | 'cancel'
  | 'previousShip'
  | 'nextShip'
  | 'previousTarget'
  | 'nextTarget'
  | 'previousAttacker'
  | 'nextAttacker'
  | 'toggleLog'
  | 'focusNearestEnemy'
  | 'focusOwnFleet'
  | 'toggleHelp'
  | 'toggleMute';

// Deps for wiring browser-level events (keyboard, sound, tooltip, online/offline)
type BrowserBindingDeps = {
  canvas: HTMLCanvasElement;
  helpCloseBtn: HTMLElement;
  helpBtn: HTMLElement;
  soundBtn: HTMLElement;
  tooltipEl: HTMLElement;
  getState: () => ClientState;
  getMap: () => SolarSystemMap;
  hasGameState: () => boolean;
  getGameState: () => import('../../shared/types/domain').GameState | null;
  getPlanningState: () => KeyboardPlanningSnapshot;
  updateTooltip: (x: number, y: number) => void;
  onKeyboardAction: (action: KeyboardAction) => void;
  onToggleHelp: () => void;
  onUpdateSoundButton: () => void;
  showToast: (message: string, type: BrowserToastType) => void;
};

const deriveSelectedShipWeakGravityToggleChoices = (
  gameState: ReturnType<BrowserBindingDeps['getGameState']>,
  map: ReturnType<BrowserBindingDeps['getMap']>,
  planning: KeyboardPlanningSnapshot,
): Record<string, boolean> | null => {
  const selectedShip =
    gameState?.ships.find((ship) => ship.id === planning.selectedShipId) ??
    null;

  if (!selectedShip) return null;

  const burn = planning.burns.get(selectedShip.id) ?? null;

  if (burn === null || selectedShip.damage.disabledTurns > 0) {
    return null;
  }

  const weakGravityChoices =
    planning.weakGravityChoices.get(selectedShip.id) ?? {};
  const course = computeCourse(selectedShip, burn, map, {
    overload: planning.overloads.get(selectedShip.id) ?? null,
    weakGravityChoices,
    destroyedBases: gameState?.destroyedBases ?? [],
  });

  const weakEffects = course.enteredGravityEffects.filter(
    (gravityEffect) => gravityEffect.strength === 'weak',
  );

  if (weakEffects.length !== 1) {
    return null;
  }

  const key = hexKey(weakEffects[0].hex);

  return {
    ...weakGravityChoices,
    [key]: !weakGravityChoices[key],
  };
};

const getGamepadShortcutAction = (
  deps: Pick<
    BrowserBindingDeps,
    'getState' | 'getMap' | 'hasGameState' | 'getGameState' | 'getPlanningState'
  >,
  control: GamepadShortcutControl,
): KeyboardAction => {
  const planning = deps.getPlanningState();
  const gameState = deps.getGameState();
  const selectedShip =
    gameState?.ships.find((ship) => ship.id === planning.selectedShipId) ??
    null;
  const selectedShipWeakGravityChoices =
    gameState && deps.getMap()
      ? deriveSelectedShipWeakGravityToggleChoices(
          gameState,
          deps.getMap(),
          planning,
        )
      : null;
  const context = {
    state: deps.getState(),
    hasGameState: deps.hasGameState(),
    typingInInput: false,
    selectedShipId: planning.selectedShipId,
    selectedShipCanOverload:
      selectedShip !== null &&
      planning.burns.get(selectedShip.id) !== null &&
      planning.burns.get(selectedShip.id) !== undefined &&
      Boolean(SHIP_STATS[selectedShip.type]?.canOverload) &&
      selectedShip.fuel >= 2 &&
      !selectedShip.overloadUsed &&
      selectedShip.damage.disabledTurns === 0,
    selectedShipBurnDirection:
      selectedShip !== null
        ? (planning.burns.get(selectedShip.id) ?? null)
        : null,
    selectedShipOverloadDirection:
      selectedShip !== null
        ? (planning.overloads.get(selectedShip.id) ?? null)
        : null,
    selectedShipWeakGravityChoices,
    combatTargetId: planning.combatTargetId,
    queuedAttackCount: planning.queuedAttacks.length,
    torpedoAccelActive: planning.torpedoAccel !== null,
    torpedoAimingActive: planning.torpedoAimingActive,
    torpedoAccelDirection: planning.torpedoAccel,
    torpedoAccelSteps: planning.torpedoAccelSteps,
    allShipsAcknowledged: (() => {
      if (!gameState) return false;
      return getOrderableShipsForPlayer(
        gameState,
        gameState.activePlayer,
      ).every(
        (ship) =>
          ship.damage.disabledTurns > 0 ||
          planning.acknowledgedShips.has(ship.id),
      );
    })(),
    allOrdnanceShipsAcknowledged: (() => {
      if (!gameState) return true;
      return getOrdnanceActionableShipIds(
        gameState,
        gameState.activePlayer,
        deps.getMap(),
      ).every((shipId) => planning.acknowledgedOrdnanceShips.has(shipId));
    })(),
    hasSelectedShip: planning.selectedShipId !== null,
  } satisfies Parameters<typeof deriveKeyboardAction>[0];

  switch (control) {
    case 'confirm':
      return deriveKeyboardAction(context, { key: 'Enter', shiftKey: false });
    case 'cancel':
      return deriveKeyboardAction(context, { key: 'Escape', shiftKey: false });
    case 'previousShip':
      return deriveKeyboardAction(context, { key: 'Tab', shiftKey: true });
    case 'nextShip':
      return deriveKeyboardAction(context, { key: 'Tab', shiftKey: false });
    case 'previousTarget':
      return deriveKeyboardAction(context, { key: '[', shiftKey: false });
    case 'nextTarget':
      return deriveKeyboardAction(context, { key: ']', shiftKey: false });
    case 'previousAttacker':
      return deriveKeyboardAction(context, { key: '{', shiftKey: false });
    case 'nextAttacker':
      return deriveKeyboardAction(context, { key: '}', shiftKey: false });
    case 'toggleLog':
      return deriveKeyboardAction(context, { key: 'l', shiftKey: false });
    case 'focusNearestEnemy':
      return deriveKeyboardAction(context, { key: 'e', shiftKey: false });
    case 'focusOwnFleet':
      return deriveKeyboardAction(context, { key: 'h', shiftKey: false });
    case 'toggleHelp':
      return deriveKeyboardAction(context, { key: '/', shiftKey: false });
    case 'toggleMute':
      return deriveKeyboardAction(context, { key: 'm', shiftKey: false });
  }
};

const bindMainBrowserEvents = (deps: BrowserBindingDeps): (() => void) =>
  bindGameClientBrowserEvents({
    canvas: deps.canvas,
    helpCloseBtn: deps.helpCloseBtn,
    helpBtn: deps.helpBtn,
    soundBtn: deps.soundBtn,
    getKeyboardAction: (event) =>
      deriveKeyboardAction(
        {
          selectedShipWeakGravityChoices: (() => {
            const gs = deps.getGameState?.();
            const map = deps.getMap();
            const planning = deps.getPlanningState();

            if (!gs || !map) return null;

            return deriveSelectedShipWeakGravityToggleChoices(
              gs,
              map,
              planning,
            );
          })(),
          state: deps.getState(),
          hasGameState: deps.hasGameState(),
          typingInInput: event.target instanceof HTMLInputElement,
          selectedShipId: deps.getPlanningState().selectedShipId,
          selectedShipCanOverload: (() => {
            const gs = deps.getGameState?.();
            const planning = deps.getPlanningState();
            const selectedShip =
              gs?.ships.find((ship) => ship.id === planning.selectedShipId) ??
              null;

            if (!selectedShip) return false;

            const burn = planning.burns.get(selectedShip.id) ?? null;

            return (
              burn !== null &&
              Boolean(SHIP_STATS[selectedShip.type]?.canOverload) &&
              selectedShip.fuel >= 2 &&
              !selectedShip.overloadUsed &&
              selectedShip.damage.disabledTurns === 0
            );
          })(),
          selectedShipBurnDirection: (() => {
            const planning = deps.getPlanningState();
            return planning.selectedShipId
              ? (planning.burns.get(planning.selectedShipId) ?? null)
              : null;
          })(),
          selectedShipOverloadDirection: (() => {
            const planning = deps.getPlanningState();
            return planning.selectedShipId
              ? (planning.overloads.get(planning.selectedShipId) ?? null)
              : null;
          })(),
          combatTargetId: deps.getPlanningState().combatTargetId,
          queuedAttackCount: deps.getPlanningState().queuedAttacks.length,
          torpedoAccelActive: deps.getPlanningState().torpedoAccel !== null,
          torpedoAimingActive: deps.getPlanningState().torpedoAimingActive,
          torpedoAccelDirection: deps.getPlanningState().torpedoAccel,
          torpedoAccelSteps: deps.getPlanningState().torpedoAccelSteps,
          allShipsAcknowledged: (() => {
            const gs = deps.getGameState?.();
            if (!gs) return false;
            const planning = deps.getPlanningState();
            return getOrderableShipsForPlayer(gs, gs.activePlayer).every(
              (s) =>
                s.damage.disabledTurns > 0 ||
                planning.acknowledgedShips.has(s.id),
            );
          })(),
          allOrdnanceShipsAcknowledged: (() => {
            const gs = deps.getGameState?.();
            if (!gs) return true;
            const planning = deps.getPlanningState();
            return getOrdnanceActionableShipIds(
              gs,
              gs.activePlayer,
              deps.getMap(),
            ).every((shipId) => planning.acknowledgedOrdnanceShips.has(shipId));
          })(),
          hasSelectedShip: deps.getPlanningState().selectedShipId !== null,
        },
        { key: event.key, shiftKey: event.shiftKey },
      ),
    onKeyboardAction: (action) => deps.onKeyboardAction(action),
    getGamepadShortcut: (control) => ({
      directAction: getGamepadShortcutAction(deps, control),
    }),
    onToggleHelp: () => deps.onToggleHelp(),
    onToggleSound: () => {
      setMuted(!isMuted());
      deps.onUpdateSoundButton();
    },
    onTooltipMove: (clientX, clientY) => deps.updateTooltip(clientX, clientY),
    onTooltipLeave: () => hide(deps.tooltipEl),
    onOffline: () => deps.showToast(TOAST.clientRuntime.offline, 'error'),
    onOnline: () => deps.showToast(TOAST.clientRuntime.backOnline, 'success'),
  });

// Light sanity check on archived-replay URL params. The server's /replay
// route already gatekeeps — this just avoids firing a session for values
// that clearly can't be a valid gameId (matches `ROOMCODE-mN` format).
const normalizeGameId = (raw: string | null): string | null => {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!/^[A-Z0-9]{5}-m\d+$/.test(trimmed)) return null;
  return trimmed;
};

export const autoJoinFromUrl = (
  joinGame: (code: string, playerToken: string | null) => void,
  spectateGame: (code: string) => void,
  viewArchivedReplay: (code: string, gameId: string) => void,
  resumeLocalGame: () => boolean,
  setMenuState: () => void,
): void => {
  const urlParams = new URLSearchParams(window.location.search);
  const code = normalizeRoomCode(urlParams.get('code'));
  const playerToken = normalizePlayerToken(urlParams.get('playerToken'));
  const viewer = urlParams.get('viewer');
  const archivedReplay = normalizeGameId(urlParams.get('archivedReplay'));

  if (code && code.length === CODE_LENGTH) {
    history.replaceState(null, '', buildGameRoute(code));
    if (archivedReplay) {
      // Archived-replay viewer path: no WebSocket, no playerToken. The
      // session controller fetches the timeline via the existing spectator
      // route (viewer=spectator) and hands it to the replay controller.
      viewArchivedReplay(code, archivedReplay);
      return;
    }
    if (viewer === 'spectator') {
      if (!isClientFeatureEnabled('spectatorMode')) {
        setMenuState();
        return;
      }
      spectateGame(code);
    } else {
      joinGame(code, playerToken);
    }
    return;
  }
  if (resumeLocalGame()) {
    return;
  }
  history.replaceState(null, '', '/');
  setMenuState();
};

export const setupServiceWorkerReload = () => {
  if (
    'serviceWorker' in navigator &&
    window.location.hostname !== 'localhost' &&
    window.location.hostname !== '127.0.0.1'
  ) {
    bindServiceWorkerControllerReload(navigator.serviceWorker, window.location);
  }
};

// Show the fixed "new version available" banner and wire its buttons.
// The banner element lives in index.html so the markup is present even
// before JS boots; this just un-hides it and owns the button handlers.
// Returns a dispose callback that hides the banner and removes listeners
// — used by tests; in production the banner lives for the rest of the
// session until the user clicks Reload or Dismiss.
const attachNewVersionBanner = (): (() => void) => {
  const banner = document.getElementById('newVersionBanner');
  if (!banner) return () => {};
  const reloadBtn = document.getElementById('newVersionReloadBtn');
  const dismissBtn = document.getElementById('newVersionDismissBtn');
  banner.removeAttribute('hidden');

  const reload = (): void => {
    window.location.reload();
  };
  const dismiss = (): void => {
    banner.setAttribute('hidden', '');
  };

  reloadBtn?.addEventListener('click', reload);
  dismissBtn?.addEventListener('click', dismiss);
  return () => {
    reloadBtn?.removeEventListener('click', reload);
    dismissBtn?.removeEventListener('click', dismiss);
    banner.setAttribute('hidden', '');
  };
};

const getCurrentBundleHash = (): string | null => {
  const scripts = Array.from(document.querySelectorAll('script[src]'));
  for (const script of scripts) {
    const src = script.getAttribute('src');
    if (!src?.includes('client.js')) continue;
    try {
      const url = new URL(src, window.location.href);
      const hash = url.searchParams.get('v')?.trim();
      if (hash) return hash;
    } catch {
      // Ignore malformed script src values and fall through to the next.
    }
  }
  return null;
};

export const setupVersionCheck = (): (() => void) | null => {
  // Same gate as service-worker registration: local dev reloads on its
  // own via esbuild/wrangler, and the build hash never changes there,
  // so there is nothing useful for this poller to signal.
  if (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  ) {
    return null;
  }

  let detachBanner: (() => void) | null = null;
  const disposePoll = startVersionCheck({
    currentHash: getCurrentBundleHash(),
    onNewVersion: () => {
      if (detachBanner) return;
      detachBanner = attachNewVersionBanner();
    },
  });

  return () => {
    disposePoll();
    detachBanner?.();
  };
};

// Subset of MainInteractionController that setupClientRuntime needs.
// Avoids importing the full controller type to keep the dependency light.
type RuntimeInteractions = {
  handleKeyboardAction: (action: KeyboardAction) => void;
  handleUIEvent: (event: UIEvent) => void;
  toggleHelp: () => void;
  showToast: (message: string, type?: BrowserToastType) => void;
  joinGame: (code: string, playerToken?: string | null) => void;
  spectateGame: (code: string) => void;
  viewArchivedReplay: (code: string, gameId: string) => void;
};

type SetupClientRuntimeInput = {
  canvas: HTMLCanvasElement;
  map: SolarSystemMap;
  tooltipEl: HTMLElement;
  renderer: Renderer;
  input: InputHandler;
  ui: UIManager;
  ctx: Pick<ClientSession, 'state' | 'gameState' | 'planningState'>;
  interactions: RuntimeInteractions;
  updateTooltip: (x: number, y: number) => void;
  onUpdateSoundButton: () => void;
  resumeLocalGame: () => boolean;
  setMenuState: () => void;
};

export const setupClientRuntime = ({
  canvas,
  map,
  tooltipEl,
  renderer,
  input,
  ui,
  ctx,
  interactions,
  updateTooltip,
  onUpdateSoundButton,
  resumeLocalGame,
  setMenuState,
}: SetupClientRuntimeInput): (() => void) => {
  renderer.setMap(map);
  input.setMap(map);
  ui.onEvent = (event) => interactions.handleUIEvent(event);

  const soundBtn = byId('soundBtn');
  onUpdateSoundButton();

  const disposeBrowserEvents = bindMainBrowserEvents({
    canvas,
    helpCloseBtn: byId('helpCloseBtn'),
    helpBtn: byId('helpBtn'),
    soundBtn,
    tooltipEl,
    getState: () => ctx.state,
    getMap: () => map,
    hasGameState: () => !!ctx.gameState,
    getGameState: () => ctx.gameState,
    getPlanningState: (): KeyboardPlanningSnapshot => ctx.planningState,
    updateTooltip,
    onKeyboardAction: (action) => interactions.handleKeyboardAction(action),
    onToggleHelp: () => interactions.toggleHelp(),
    onUpdateSoundButton,
    showToast: (message, type) => interactions.showToast(message, type),
  });

  initAudio();
  renderer.start();
  autoJoinFromUrl(
    (code, playerToken) => interactions.joinGame(code, playerToken),
    (code) => {
      interactions.spectateGame(code);
      interactions.showToast(TOAST.spectator.urlWatchOnly, 'info');
    },
    (code, gameId) => interactions.viewArchivedReplay(code, gameId),
    resumeLocalGame,
    setMenuState,
  );

  return () => {
    disposeBrowserEvents();
  };
};
